import { Draft } from 'immer'
import { computeEndDateFromDuration } from '@opallabs/lambda'
import { noCategoryKey, prePlanningBlockKey } from '..'
import { PlanResponse } from '../types'
import { resolveOverlappingBlocks } from './resolveOverlappingBlocks'

export const changeBlockCategory = (
  blockId: string,
  {
    categoryIdToInsertInto,
    categoryIdToRemoveFrom,
  }: {
    categoryIdToInsertInto?: string | null
    categoryIdToRemoveFrom?: string | null
  }
) => {
  return (draftResponse: Draft<PlanResponse>) => {
    // Moving a block within its own category updates it in place, so the
    // bucket order stays the same.
    if (
      categoryIdToRemoveFrom !== undefined &&
      categoryIdToRemoveFrom !== categoryIdToInsertInto
    ) {
      draftResponse = removeBlockFromCategory(
        blockId,
        categoryIdToRemoveFrom ?? prePlanningBlockKey
      )(draftResponse)

      // When removing from pre-planning, also remove from no_category bucket
      if (categoryIdToRemoveFrom === null) {
        draftResponse = removeBlockFromCategory(
          blockId,
          noCategoryKey
        )(draftResponse)
      }
    }

    if (categoryIdToInsertInto !== undefined) {
      draftResponse = addBlockToCategory(
        blockId,
        categoryIdToInsertInto ?? prePlanningBlockKey
      )(draftResponse)
    }

    return draftResponse
  }
}

export const removeBlockFromCategory = (
  blockId: string,
  categoryId: string
) => {
  return (draftResponse: Draft<PlanResponse>) => {
    const blocksForCategory =
      draftResponse.data.blocks.by_category_with_position[categoryId]

    if (!blocksForCategory) return draftResponse

    if (!blocksForCategory.some(({ id }) => id === blockId)) {
      return draftResponse
    }

    draftResponse.data.blocks.by_category_with_position[categoryId] =
      blocksForCategory.filter(({ id }) => id !== blockId)

    return draftResponse
  }
}

export const addBlockToCategory = (blockId: string, categoryId: string) => {
  return (draftResponse: Draft<PlanResponse>) => {
    const block = draftResponse.data.blocks.by_id[blockId]
    const blocksByCategory = draftResponse.data.blocks.by_category_with_position

    if (!block) return draftResponse

    if (!blocksByCategory[categoryId]) {
      blocksByCategory[categoryId] = []
    }

    const blocksForCategory = blocksByCategory[categoryId]
    const dates = {
      start_at: block.start_at,
      duration: block.duration,
      end_at: computeEndDateFromDuration(block.start_at, block.duration),
    }
    // The BFF can already list the block here: a block with a swimlane but no
    // start date is in both the pre-planning bucket and its swimlane bucket.
    // Update it in place so the bucket order, and the row the bucket gave it,
    // stay the same.
    const blockRefIndex = blocksForCategory.findIndex(
      ({ id }) => id === blockId
    )

    if (blockRefIndex === -1) {
      blocksForCategory.push({
        id: blockId,
        ...dates,
        row_index: block.row_index,
      })
    } else {
      blocksForCategory[blockRefIndex] = {
        ...blocksForCategory[blockRefIndex],
        ...dates,
      }
    }

    if (categoryId !== prePlanningBlockKey) {
      draftResponse = resolveOverlappingBlocks(
        blockId,
        categoryId
      )(draftResponse)
    }

    return draftResponse
  }
}
